import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { API } from 'aws-amplify';
import { RequestStatusObject, createRequestStatusObject, buildReducers } from '../utils';

export interface ReconcileRun {
	sk?: string;
	startedAt?: string;
	finishedAt?: string;
	durationMs?: number;
	trigger?: string;
	scannedValidPaths?: number;
	orphanCandidates?: number;
	skippedRecent?: number;
	deleted?: number;
	dryRun?: boolean;
	disabled?: boolean;
	minAgeHours?: number;
	maxDeletesPerRun?: number;
}

export interface ReconcileConfig {
	enabled: boolean;
	dryRun: boolean;
	minAgeHours: number;
	maxDeletesPerRun: number;
}

export interface SystemState {
	latest: ReconcileRun | null;
	history: ReconcileRun[];
	config: ReconcileConfig | null;
	getReconcileRequest: RequestStatusObject;
	saveReconcileRequest: RequestStatusObject;
	runReconcileRequest: RequestStatusObject;
}

const initialState: SystemState = {
	latest: null,
	history: [],
	config: null,
	getReconcileRequest: createRequestStatusObject(),
	saveReconcileRequest: createRequestStatusObject(),
	runReconcileRequest: createRequestStatusObject()
};

export const getStorageReconcile = createAsyncThunk('system/getStorageReconcile', async () => {
	const response = await API.get('inspirusapi', '/admins/system/storage-reconcile', {
		headers: { 'Content-Type': 'application/json' }
	});
	return response as { latest: ReconcileRun | null; history: ReconcileRun[]; config: ReconcileConfig };
});

export const saveStorageReconcileConfig = createAsyncThunk(
	'system/saveStorageReconcileConfig',
	async (config: ReconcileConfig) => {
		const response = await API.put('inspirusapi', '/admins/system/storage-reconcile/config', {
			headers: { 'Content-Type': 'application/json' },
			body: config
		});
		return response as { message: string; config: ReconcileConfig };
	}
);

export const runStorageReconcile = createAsyncThunk('system/runStorageReconcile', async () => {
	const response = await API.post('inspirusapi', '/admins/system/storage-reconcile/run', {
		headers: { 'Content-Type': 'application/json' },
		body: {}
	});
	return response as { message: string };
});

const systemSlice = createSlice({
	name: 'system',
	initialState,
	reducers: {
		resetSystem: () => initialState
	},
	extraReducers: (builder) => {
		buildReducers(builder, getStorageReconcile, 'getReconcileRequest', {
			onFulfilled: (state, payload) => {
				state.latest = payload.latest ?? null;
				state.history = payload.history ?? [];
				state.config = payload.config ?? null;
			}
		});
		buildReducers(builder, saveStorageReconcileConfig, 'saveReconcileRequest', {
			onFulfilled: (state, payload) => {
				state.config = payload.config ?? state.config;
			}
		});
		buildReducers(builder, runStorageReconcile, 'runReconcileRequest');
	}
});

export const { resetSystem } = systemSlice.actions;
export default systemSlice.reducer;
